/**
 * AuthModal — sign-in / sign-up / password-recovery modal extracted from App.js.
 *
 * Self-contained: owns ALL of its own state (login/signup mode, form fields,
 * practitioner two-step flow, recovery flow). Parent only controls `open` and
 * passes a session_id so we can link the anonymous session to the user post-auth.
 *
 * Iter-43 split: identical behaviour to the previous inline modal in App.js —
 * same data-testids, same handlers, same wire-format calls. The only diff is
 * the state lives here instead of in TopBar.
 */
import React, { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { AuthContext } from "@/context/AuthContext";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthModal = ({ open, onClose, sessionId }) => {
  const { login, register } = useContext(AuthContext);

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Recovery flow
  const [showRecover, setShowRecover] = useState(false);
  const [recoverStep, setRecoverStep] = useState("email"); // email | chooseEmail | emailSent | question | newpass | done
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Practitioner two-step signup
  const [isPractitioner, setIsPractitioner] = useState(false);
  const [signupStep, setSignupStep] = useState("basic"); // basic | practitioner
  const [practiceType, setPracticeType] = useState("EMDR");
  const [pracAddress, setPracAddress] = useState("");
  const [pracCity, setPracCity] = useState("");
  const [pracState, setPracState] = useState("");
  const [pracZip, setPracZip] = useState("");
  const [pracPhone, setPracPhone] = useState("");
  const [pracBio, setPracBio] = useState("");

  const [referralCode, setReferralCode] = useState("");

  // Auto-fill referral code from ?ref=email URL param on mount
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref) setReferralCode(ref);
    } catch { /* ignore */ }
  }, []);

  // Reset signup step whenever the modal closes or the user toggles login/signup.
  useEffect(() => {
    if (!open || isLogin) setSignupStep("basic");
  }, [open, isLogin]);

  const resetForm = () => {
    setEmail(""); setPassword(""); setName("");
    setIsPractitioner(false); setSignupStep("basic");
    setPracAddress(""); setPracCity(""); setPracState(""); setPracZip("");
    setPracPhone(""); setPracBio("");
    setReferralCode("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isLogin && isPractitioner && signupStep === "basic") {
      setSignupStep("practitioner");
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        const extra = isPractitioner ? {
          is_practitioner: true,
          practice_type: practiceType,
          address: pracAddress,
          city: pracCity,
          state: pracState,
          zip: pracZip,
          phone: pracPhone,
          bio: pracBio,
        } : {};
        await register(email, password, name, extra);
      }
      // Link the anonymous session_id to this user account so progress carries over,
      // exempt status applies, and the same session_id is returned on next login.
      await fetch(`${API_BASE}/auth/link-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      });
      const refCode = (referralCode || "").trim().toLowerCase();
      if (!isLogin && refCode && refCode !== email.trim().toLowerCase()) {
        try {
          await fetch(`${API_BASE}/referral/apply`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: sessionId, referral_code: refCode }),
          });
        } catch { /* referral failure is non-fatal */ }
      }
      onClose();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const lookupSecurityQuestion = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/security-question/${encodeURIComponent(email)}`);
      const data = await res.json();
      if (data.has_question) {
        setSecurityQuestion(data.question);
        setRecoverStep("question");
        setError("");
      } else {
        setRecoverStep("chooseEmail");
        setError("");
      }
    } catch (err) {
      console.warn("[Recover] lookup failed:", err?.message || err);
      setRecoverStep("chooseEmail");
    }
  };

  const sendResetEmail = async () => {
    try {
      await fetch(`${API_BASE}/auth/request-password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, app_url: window.location.origin }),
      });
      setRecoverStep("emailSent");
      setError("");
    } catch (err) {
      console.warn("[Recover] email reset failed:", err?.message || err);
      setError("Could not send reset email. Please try again.");
    }
  };

  const submitNewPassword = async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/recover-with-security`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, answer: securityAnswer, new_password: newPassword }),
      });
      if (res.ok) { setRecoverStep("done"); setError(""); }
      else { const d = await res.json(); setError(d.detail || "Recovery failed"); }
    } catch (_err) { setError("Something went wrong"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-3xl p-8 max-w-sm w-[90%] mx-4"
            style={{ boxShadow: "0 24px 64px rgba(79,55,139,0.3)" }}
            onClick={e => e.stopPropagation()}
            data-testid="auth-modal"
          >
            <h2 className="text-xl font-bold mb-1 text-center" style={{ fontFamily: "'Fredoka', sans-serif", color: "#4F378B" }}>
              {isLogin ? "Welcome Back" : "Join Primordeia"}
            </h2>
            <p className="text-xs text-center mb-5" style={{ color: "#8B7DA8" }}>
              {isLogin ? "Sign in to continue your journey" : "Create your account to begin"}
            </p>

            {error && <div className="text-xs text-center mb-3 p-2 rounded-lg" style={{ backgroundColor: "#FDE8E8", color: "#C0392B" }}>{error}</div>}

            <form onSubmit={handleSubmit} className="space-y-3">
              {!isLogin && (
                <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: "#E8E0F0", fontFamily: "'Nunito', sans-serif" }}
                  data-testid="auth-name-input" />
              )}
              <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required
                className="w-full px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: "#E8E0F0", fontFamily: "'Nunito', sans-serif" }}
                data-testid="auth-email-input" />
              <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full px-4 py-2.5 rounded-xl border text-sm" style={{ borderColor: "#E8E0F0", fontFamily: "'Nunito', sans-serif" }}
                data-testid="auth-password-input" />

              {!isLogin && signupStep === "basic" && (
                <>
                  <input type="text" placeholder="Referral name OR code (100 free lotuses)" value={referralCode}
                    onChange={e => setReferralCode(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border text-sm"
                    style={{ borderColor: "#E8E0F0", fontFamily: "'Nunito', sans-serif" }}
                    data-testid="auth-referral-input" />
                  {referralCode && (
                    <div className="text-[10px] -mt-1 px-1" style={{ color: "#7BC97B", fontFamily: "'Nunito', sans-serif" }}>
                      ✨ 100 free lotuses with <strong>{referralCode}</strong> on signup
                    </div>
                  )}
                  <label className="flex items-start gap-2 px-1 cursor-pointer select-none">
                    <input type="checkbox" checked={isPractitioner} onChange={e => setIsPractitioner(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-purple-700"
                      data-testid="auth-practitioner-checkbox" />
                    <span className="text-[11px] leading-snug" style={{ color: "#5C5470" }}>
                      I am a <strong>practitioner of healing arts who does not use prescription medicine</strong> in their practice <em>and would like to advertise</em>. <span style={{ color: "#8B7DA8" }}>(checkbox not required)</span>
                    </span>
                  </label>
                </>
              )}

              {!isLogin && signupStep === "practitioner" && (
                <div className="space-y-2 p-3 rounded-xl" style={{ backgroundColor: "#F0E8F8" }} data-testid="practitioner-fields">
                  <button type="button" onClick={() => setSignupStep("basic")}
                    className="flex items-center gap-1 text-[11px] font-semibold mb-1" style={{ color: "#7B5EA7" }}
                    data-testid="practitioner-back-button">
                    <ArrowLeft size={12} /> Back
                  </button>
                  <div className="rounded-lg p-2.5 mb-1" style={{ backgroundColor: "#FFF8E7", border: "1px solid #D4A574" }}>
                    <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "#3D2A0E", fontFamily: "'Cinzel', serif" }}>
                      ✦ Premium Listing Pitch
                    </div>
                    <p className="text-[11px] leading-snug" style={{ color: "#3D2A0E" }}>
                      We will advertise your practice in the EMDR Trauma Chill Zone for your ZIP. Your listing goes live the moment you create the account — <strong>first 30 days free</strong>, then $7.59/mo on the card you'll add later. Seekers see you first, above the public registry.
                    </p>
                  </div>
                  <select value={practiceType} onChange={e => setPracticeType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-xs"
                    style={{ borderColor: "#D4C4E8", fontFamily: "'Nunito', sans-serif" }}
                    data-testid="practitioner-practice-type">
                    <option value="EMDR">EMDR</option>
                    <option value="Somatic Experiencing">Somatic Experiencing</option>
                    <option value="Internal Family Systems (IFS)">Internal Family Systems (IFS)</option>
                    <option value="Brainspotting">Brainspotting</option>
                    <option value="Breathwork">Breathwork</option>
                    <option value="Sound Healing">Sound Healing</option>
                    <option value="Reiki">Reiki</option>
                    <option value="Trauma-Informed Yoga">Trauma-Informed Yoga</option>
                    <option value="Other">Other</option>
                  </select>
                  <input type="text" placeholder="Street address" value={pracAddress} onChange={e => setPracAddress(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
                    data-testid="practitioner-address" />
                  <div className="flex gap-2">
                    <input type="text" placeholder="City" value={pracCity} onChange={e => setPracCity(e.target.value)}
                      className="flex-[2] px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
                      data-testid="practitioner-city" />
                    <input type="text" placeholder="State" maxLength={2} value={pracState}
                      onChange={e => setPracState(e.target.value.toUpperCase())}
                      className="flex-[1] px-3 py-2 rounded-lg border text-xs uppercase" style={{ borderColor: "#D4C4E8" }}
                      data-testid="practitioner-state" />
                    <input type="text" inputMode="numeric" pattern="[0-9]*" placeholder="ZIP" maxLength={5}
                      value={pracZip} onChange={e => setPracZip(e.target.value.replace(/\D/g, ""))}
                      className="flex-[1.2] px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
                      data-testid="practitioner-zip" />
                  </div>
                  <input type="tel" placeholder="Phone" value={pracPhone} onChange={e => setPracPhone(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-xs" style={{ borderColor: "#D4C4E8" }}
                    data-testid="practitioner-phone" />
                  <textarea placeholder="Short bio (1-2 sentences about your practice)" value={pracBio}
                    onChange={e => setPracBio(e.target.value)} rows={2} maxLength={500}
                    className="w-full px-3 py-2 rounded-lg border text-xs resize-none" style={{ borderColor: "#D4C4E8" }}
                    data-testid="practitioner-bio" />
                </div>
              )}

              {isLogin && !showRecover && (
                <button type="button" onClick={() => { setShowRecover(true); setRecoverStep("email"); setError(""); }}
                  className="w-full text-right text-xs font-semibold -mt-1" style={{ color: "#7B5EA7" }}
                  data-testid="forgot-password-link">
                  Forgot password?
                </button>
              )}

              {showRecover && (
                <div className="p-3 rounded-xl space-y-2" style={{ backgroundColor: "#F0E8F8" }}>
                  {recoverStep === "email" && (
                    <>
                      <p className="text-[10px] text-center" style={{ color: "#5C5470" }}>Enter your email to recover</p>
                      <div className="flex gap-1">
                        <input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)}
                          className="flex-1 px-3 py-1.5 rounded-lg border text-xs" style={{ borderColor: "#E8E0F0" }}
                          data-testid="recover-email-input" />
                        <button type="button" onClick={lookupSecurityQuestion}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                          style={{ backgroundColor: "#4F378B" }}
                          data-testid="recover-next-button">Next</button>
                      </div>
                    </>
                  )}
                  {recoverStep === "chooseEmail" && (
                    <>
                      <p className="text-[10px]" style={{ color: "#5C5470" }}>
                        You haven&rsquo;t set a security question yet. We&rsquo;ll send a password-reset link to <strong>{email}</strong> instead.
                      </p>
                      <button type="button" onClick={sendResetEmail}
                        className="w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ backgroundColor: "#4F378B" }}
                        data-testid="recover-email-send">Send reset link</button>
                    </>
                  )}
                  {recoverStep === "emailSent" && (
                    <div className="text-center space-y-2">
                      <p className="text-xs font-semibold" style={{ color: "#4CAF50" }}>
                        ✉ If <strong>{email}</strong> is registered, a reset link is on its way.
                      </p>
                      <p className="text-[10px] italic" style={{ color: "#5C5470" }}>
                        The email comes from <strong>papaya@primordeia.com</strong>. If you don&rsquo;t see it in 2-3 minutes, please check your <strong>spam / junk folder</strong> and mark it as &ldquo;not spam&rdquo; — Proton and Gmail tend to filter new domains aggressively.
                      </p>
                    </div>
                  )}
                  {recoverStep === "question" && (
                    <>
                      <p className="text-[10px] font-semibold" style={{ color: "#4F378B" }}>{securityQuestion}</p>
                      <input type="text" placeholder="Your answer" value={securityAnswer} onChange={e => setSecurityAnswer(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border text-xs" style={{ borderColor: "#E8E0F0" }}
                        data-testid="recover-answer-input" />
                      <button type="button" onClick={() => setRecoverStep("newpass")}
                        className="w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ backgroundColor: "#4F378B" }}
                        data-testid="recover-to-password">Next</button>
                      <button type="button" onClick={() => setRecoverStep("chooseEmail")}
                        className="w-full text-[10px] italic underline mt-1"
                        style={{ color: "#7B5EA7" }}
                        data-testid="recover-use-email-instead">
                        Don&rsquo;t remember the answer? Email me a reset link instead.
                      </button>
                    </>
                  )}
                  {recoverStep === "newpass" && (
                    <>
                      <p className="text-[10px]" style={{ color: "#5C5470" }}>Set your new password</p>
                      <input type="password" placeholder="New password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                        className="w-full px-3 py-1.5 rounded-lg border text-xs" style={{ borderColor: "#E8E0F0" }}
                        data-testid="recover-new-password" />
                      <button type="button" onClick={submitNewPassword}
                        className="w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ backgroundColor: "#4F378B" }}
                        data-testid="recover-submit">Reset Password</button>
                    </>
                  )}
                  {recoverStep === "done" && (
                    <p className="text-xs text-center" style={{ color: "#4CAF50" }}>
                      Password reset! You can now sign in with your new password.
                    </p>
                  )}
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm"
                style={{ background: "linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)", opacity: loading ? 0.7 : 1 }}
                data-testid="auth-submit-button"
              >
                {(() => {
                  if (loading) return "Please wait...";
                  if (isLogin) return "Sign In";
                  if (isPractitioner && signupStep === "basic") return "Continue — practice details";
                  return "Create Account";
                })()}
              </button>
            </form>

            <button onClick={() => { setIsLogin(!isLogin); setError(""); setShowRecover(false); setRecoverStep("email"); }}
              className="w-full text-center text-xs mt-4 font-semibold" style={{ color: "#7B5EA7" }}>
              {isLogin ? "Don't have an account? Sign Up" : "Already have an account? Sign In"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
